import { AdminCuisineTypes } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /cuisine-types/*.
 *
 * Глобальный справочник типов кухонь (платформенные, без cafeId).
 * Все ответы парсятся через Zod, чтобы поймать drift контрактов.
 * DELETE отдаёт HTTP 204 без тела — для него парсинг body пропускаем
 * (api-contracts.md): EmptyResponseSchema не принимает null от 204.
 */
export const cuisineTypesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listCuisineTypes: build.query<
            AdminCuisineTypes.GetCuisineTypesResponse,
            AdminCuisineTypes.GetCuisineTypesRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.isActive !== undefined) {
                    search.isActive = params.isActive ? 'true' : 'false';
                }
                return {
                    url: '/cuisine-types',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): AdminCuisineTypes.GetCuisineTypesResponse =>
                parseWithSchema(AdminCuisineTypes.GetCuisineTypesResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((c) => ({
                            type: 'CuisineType' as const,
                            id: c.id
                        })),
                        { type: 'CuisineType' as const, id: 'LIST' }
                    ]
                    : [{ type: 'CuisineType' as const, id: 'LIST' }]
        }),

        createCuisineType: build.mutation<
            AdminCuisineTypes.CreateCuisineTypeResponse,
            AdminCuisineTypes.CreateCuisineTypeRequest
        >({
            query: (body) => ({
                url: '/cuisine-types',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): AdminCuisineTypes.CreateCuisineTypeResponse =>
                parseWithSchema(AdminCuisineTypes.CreateCuisineTypeResponseSchema, raw),
            invalidatesTags: [{ type: 'CuisineType', id: 'LIST' }]
        }),

        updateCuisineType: build.mutation<
            AdminCuisineTypes.UpdateCuisineTypeResponse,
            { id: string; body: AdminCuisineTypes.UpdateCuisineTypeRequest }
        >({
            query: ({ id, body }) => ({
                url: `/cuisine-types/${id}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (raw: unknown): AdminCuisineTypes.UpdateCuisineTypeResponse =>
                parseWithSchema(AdminCuisineTypes.UpdateCuisineTypeResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'CuisineType', id: arg.id },
                { type: 'CuisineType', id: 'LIST' }
            ]
        }),

        deleteCuisineType: build.mutation<
            AdminCuisineTypes.DeleteCuisineTypeResponse,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `/cuisine-types/${id}`,
                method: 'DELETE'
            }),
            // HTTP 204 — тела нет, парсить нечего (api-contracts.md).
            transformResponse: (): AdminCuisineTypes.DeleteCuisineTypeResponse => ({}),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'CuisineType', id: arg.id },
                { type: 'CuisineType', id: 'LIST' }
            ]
        }),

        reorderCuisineTypes: build.mutation<
            AdminCuisineTypes.ReorderCuisineTypesResponse,
            AdminCuisineTypes.ReorderCuisineTypesRequest
        >({
            query: (body) => ({
                url: '/cuisine-types/reorder',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): AdminCuisineTypes.ReorderCuisineTypesResponse =>
                parseWithSchema(AdminCuisineTypes.ReorderCuisineTypesResponseSchema, raw),
            invalidatesTags: [{ type: 'CuisineType', id: 'LIST' }]
        })
    })
});

export const {
    useListCuisineTypesQuery,
    useCreateCuisineTypeMutation,
    useUpdateCuisineTypeMutation,
    useDeleteCuisineTypeMutation,
    useReorderCuisineTypesMutation
} = cuisineTypesApi;
